import {
    Row,
    Col,
    Card,
    Table,
    Space,
    Select,
    Tag,
    message
} from "antd";
import {useEffect, useState} from "react";
import accountRestService from "../services/acc-rest.service";
import DuplicateMembersRow from "../components/tables/DuplicateMembersRow";

// How many clusters to ask the server for. The endpoint ranks before it truncates,
// so a larger value only ever appends lower-priority rows.
const MAX_ACCOUNTS_OPTIONS = [25, 50, 100, 200, 500];

function DuplicateAccounts() {
    const [rows, setRows] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [maxAccounts, setMaxAccounts] = useState(50);

    useEffect(() => {
        const loadDuplicates = async () => {
            setLoading(true);
            try {
                const result = await accountRestService.getTopDuplicateAccounts(maxAccounts);
                setRows(result || []);
            } catch (error) {
                message.error({content: "Failed to load duplicate accounts: " + error, duration: 5});
                setRows([]);
            } finally {
                setLoading(false);
            }
        };
        loadDuplicates();
    }, [maxAccounts]);

    // Filter options come from the loaded rows, not a hardcoded list, so a new match
    // rule added server-side appears here without a frontend change.
    const matchedOnFilters = [...new Set(rows.flatMap((row) => row.matchedOn || []))]
        .sort()
        .map((rule) => ({text: rule, value: rule}));

    const columns = [
        {
            title: "Full Name",
            dataIndex: "",
            render: (record) => (
                <div className="author-info">
                    <p>{record.name}</p>
                </div>
            )
        },
        {
            title: "Duplicates",
            dataIndex: "",
            sorter: (a, b) => (a.neighbourCount ?? 0) - (b.neighbourCount ?? 0),
            render: (record) => (
                <div align={"center"}>
                    <Tag color="processing">{record.neighbourCount}</Tag>
                </div>
            )
        },
        {
            title: "Rules Matched",
            dataIndex: "",
            sorter: (a, b) => (a.rulesMatched ?? 0) - (b.rulesMatched ?? 0),
            render: (record) => (
                <div align={"center"}>
                    <Tag >{record.rulesMatched}</Tag>
                </div>
            )
        },
        {
            title: "Matched On",
            dataIndex: "",
            filters: matchedOnFilters,
            filterMultiple: true,
            onFilter: (value, record) => record.matchedOn?.includes(value),
            render: (record) => (
                <Space size={[0, 4]} wrap>
                    {record.matchedOn?.map((rule) => <Tag key={rule}>{rule}</Tag>)}
                </Space>
            )
        }
    ];

    return (
        <div className="tabled">
            <Row gutter={[24, 0]}>
                <Col span={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title={`Duplicate Accounts (${rows.length})`}
                        extra={
                            <Space>
                                <Select
                                    value={maxAccounts}
                                    onChange={(value) => setMaxAccounts(value)}
                                    style={{minWidth: 120}}
                                    options={MAX_ACCOUNTS_OPTIONS.map((n) => ({
                                        value: n,
                                        label: `Top ${n}`
                                    }))}
                                />
                            </Space>
                        }
                    >
                        <div className="table-responsive">
                            <Table
                                columns={columns}
                                dataSource={rows}
                                rowKey={(record) => record.userId}
                                loading={isLoading}
                                size="small"
                                expandable={{
                                    expandedRowClassName: () => "duplicate-expanded-row",
                                    expandedRowRender: (record) =>
                                        <DuplicateMembersRow userId={record.userId}/>
                                }}
                                pagination={{
                                    defaultPageSize: 20,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "20", "50", "100"],
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                                }}
                                className="ant-border-space"
                            />
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default DuplicateAccounts;
